import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types
export interface SenderEmail {
  id: number
  name: string
  email: string
  email_signature: string
  account_type: string
  status: string
  /** Local warming lifecycle, independent of the EmailBison-synced `status`.
   * 'warming' accounts are badged and blocked from campaign attachment. */
  lifecycle_state: 'warming' | 'active'
  daily_limit: number
  emails_sent: number
  opened: number
  unique_opens: number
  replied: number
  unique_replies: number
  bounced: number
  unsubscribed: number
  total_leads_contacted: number
  interested: number
  created_at: string
  updated_at: string
  active_campaigns: { id: number; name: string }[]
  /** Non-blocking warnings (e.g. phone reformat notes) returned on update. */
  signature_warnings?: string[]
}

export interface SyncSenderEmailsResponse {
  data: SenderEmail[]
  created: number
  updated: number
}

export interface PaginatedSenderEmailsResponse {
  count: number
  next: string | null
  previous: string | null
  results: SenderEmail[]
}

export interface AttachSenderEmailsPayload {
  sender_email_ids: number[]
}

export interface AttachSenderEmailsResponse {
  success: boolean
  message: string
  attached_count: number
  skipped_count: number
}

export interface UpdateSenderEmailPayload {
  name?: string
  daily_limit?: number
  email_signature?: string
}

export interface WorkspaceSenderEmailsParams {
  page?: number
  limit?: number
}

// Query keys
export const senderEmailKeys = {
  all: ['senderEmails'] as const,
  workspace: () => [...senderEmailKeys.all, 'workspace'] as const,
  byCampaign: (bisonCampaignId: number) =>
    [...senderEmailKeys.all, 'campaign', bisonCampaignId] as const,
}

// API calls
const getWorkspaceSenderEmails = async (
  params?: WorkspaceSenderEmailsParams
): Promise<PaginatedSenderEmailsResponse> => {
  return baseAPICall<PaginatedSenderEmailsResponse>(() =>
    clientInstance.get('/emailbison/sender-emails/', { params })
  )
}

const syncWorkspaceSenderEmails =
  async (): Promise<SyncSenderEmailsResponse> => {
    return baseAPICall<SyncSenderEmailsResponse>(() =>
      clientInstance.post('/emailbison/sender-emails/sync/')
    )
  }

const getCampaignSenderEmails = async (
  bisonCampaignId: number
): Promise<PaginatedSenderEmailsResponse> => {
  return baseAPICall<PaginatedSenderEmailsResponse>(() =>
    clientInstance.get(
      `/emailbison/campaigns/${bisonCampaignId}/sender-emails/`
    )
  )
}

const attachCampaignSenderEmails = async (
  bisonCampaignId: number,
  payload: AttachSenderEmailsPayload
): Promise<AttachSenderEmailsResponse> => {
  return baseAPICall<AttachSenderEmailsResponse>(() =>
    clientInstance.post(
      `/emailbison/campaigns/${bisonCampaignId}/sender-emails/attach/`,
      payload
    )
  )
}

const detachCampaignSenderEmail = async (
  bisonCampaignId: number,
  senderEmailId: number
): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.delete(
      `/emailbison/campaigns/${bisonCampaignId}/sender-emails/${senderEmailId}/`
    )
  )
}

const updateSenderEmail = async (
  senderEmailId: number,
  payload: UpdateSenderEmailPayload
): Promise<SenderEmail> => {
  return baseAPICall<SenderEmail>(
    () =>
      clientInstance.patch(
        `/emailbison/sender-emails/${senderEmailId}/`,
        payload
      ),
    { disableNotifyGenericError: true }
  )
}

const deleteSenderEmail = async (senderEmailId: number): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.delete(`/emailbison/sender-emails/${senderEmailId}/`)
  )
}

// Hooks
export const useGetWorkspaceSenderEmails = (
  params?: WorkspaceSenderEmailsParams
) => {
  return useQuery({
    // Page and limit are part of the key so each page/page-size is cached
    // separately (a limit:1000 lifecycle lookup must not collide with the
    // default 100-row page-1 callers); sync/mutation invalidation still matches
    // via the shared `workspace()` key prefix.
    queryKey: [
      ...senderEmailKeys.workspace(),
      params?.page ?? 1,
      params?.limit ?? null,
    ],
    queryFn: () => getWorkspaceSenderEmails(params),
    placeholderData: keepPreviousData,
  })
}

export const useSyncWorkspaceSenderEmails = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: syncWorkspaceSenderEmails,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.workspace(),
      })
    },
  })
}

export const useGetCampaignSenderEmails = (bisonCampaignId: number) => {
  return useQuery({
    queryKey: senderEmailKeys.byCampaign(bisonCampaignId),
    queryFn: () => getCampaignSenderEmails(bisonCampaignId),
    enabled: bisonCampaignId > 0,
  })
}

export const useAttachCampaignSenderEmails = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AttachSenderEmailsPayload) =>
      attachCampaignSenderEmails(bisonCampaignId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.byCampaign(bisonCampaignId),
      })
    },
  })
}

export const useDetachCampaignSenderEmail = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (senderEmailId: number) =>
      detachCampaignSenderEmail(bisonCampaignId, senderEmailId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.byCampaign(bisonCampaignId),
      })
    },
  })
}

export const useUpdateSenderEmail = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      senderEmailId,
      payload,
    }: {
      senderEmailId: number
      payload: UpdateSenderEmailPayload
    }) => updateSenderEmail(senderEmailId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.all,
      })
    },
  })
}

export const useDeleteSenderEmail = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (senderEmailId: number) => deleteSenderEmail(senderEmailId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.all,
      })
    },
  })
}
